import type { BootErrorEnvelope, SurfaceDecision } from './kernel-contract.js';

/**
 * CAOS-662 — the workspace's own error normalizer.
 *
 * The Engine normalizes every boot failure it models into an envelope on the `error` decision.
 * A few failures only the workspace can see — the Engine could not be reached, answered with
 * something that was not a decision, or decided on a surface nothing here can draw — and they used
 * to reach the screen as codes namespaced under nothing (`boot.unreachable`,
 * `surface.unregistered.<name>`). They pass through here instead, and come out as the same
 * envelope, under codes the kernel catalogue (`kernel/src/errors/catalog.ts`) names with the same
 * class, fault, retriable flag and message. The workspace cannot import that catalogue — it
 * typechecks hermetically — so `host/test/caos-662-boot-error-codes.test.ts` holds these to it.
 */

/** The Engine could not be reached, or did not answer with a decision. */
export const ENGINE_UNREACHABLE: BootErrorEnvelope = {
  code: 'integration.engine_unreachable',
  class: 'integration',
  message: "We couldn't reach the Engine. Try again in a moment.",
  fault: 'platform',
  retriable: true,
};

/** The Engine decided on a surface this workspace has no renderer for. */
export const SURFACE_UNREGISTERED: BootErrorEnvelope = {
  code: 'internal.surface_unregistered',
  class: 'internal',
  message: "This screen has nothing to show it yet. We've logged it.",
  fault: 'platform',
  retriable: false,
};

/**
 * An `error` decision that arrived without its envelope — an Engine that predates it. Nothing
 * says what went wrong, so it is the catch-all rather than a guess at the fault or the retry.
 */
export const UNEXPLAINED_BOOT_FAILURE: BootErrorEnvelope = {
  code: 'internal.unexpected',
  class: 'internal',
  message: "Something went wrong on our end. We're looking into it.",
  fault: 'platform',
  retriable: false,
};

/** The envelope an `error` decision is rendered from. */
export function bootErrorOf(decision: Extract<SurfaceDecision, { surface: 'error' }>): BootErrorEnvelope {
  return decision.error ?? UNEXPLAINED_BOOT_FAILURE;
}
